import { AxiosInstance } from "axios";
import { HttpHelper } from "../../../../Core/Http/HttpHelper";
import { ApplicationLogEntry, AuthResponse, ErrorResponse, LeadToLive, LeadToLiveStep, RegisterError, RegisterFieldErrors, RegisterGDAPMailMessage } from "../../../../Model/CommonModel";
import { AdminService } from "../../../../ApplicationCode/Admin/AdminService";
import { BalanceSheet, OpenAPICompanyITAdvancedData } from "../../../../Model/OpenAPIModel";
import { IComboBoxOption } from "@fluentui/react";
import { PostalCode } from "../../../../Model/SystemModels";
import { CountryRegion } from "../../../../Model/CustomerModels";
import { SoSmartMicrosoftLogin } from "./RegisterLoginMicrosoft";
import { CookieService } from "../../../../Core/Cookie/CookieHelper";
import { HttpConstants } from "../../../../Core/Http/HttpConstants";
import jwt from 'jwt-decode';
import { t } from "i18next";

export class RegisterService {

    constructor() {}

    //#region API

    public static getQualificationStatus(currentStep: LeadToLiveStep, abortSignal: AbortSignal): Promise<LeadToLive> {
        return new Promise<LeadToLive>((resolve, reject) => {
            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.get(`/api/system/lead/qualification/status/${currentStep}`, {signal: abortSignal})
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error.response.data);
                });
        });
    }

    public static getTrialStatus(currentStep: LeadToLiveStep, abortSignal: AbortSignal): Promise<LeadToLive> {
        return new Promise<LeadToLive>((resolve, reject) => {
            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.get(`/api/system/lead/trial/status/${currentStep}`, {signal: abortSignal})
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error.response.data);
                });
        });
    }

    public static saveQualificationData(dataToSave: LeadToLive, abortSignal: AbortSignal): Promise<LeadToLive> {
        return new Promise<LeadToLive>((resolve, reject) => {
            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.post("/api/system/lead/qualification/save", dataToSave, {signal: abortSignal})
                .then((response) => {
                    let nextStep: LeadToLiveStep = this.getCurrentStep(dataToSave);

                    this.getQualificationStatus(nextStep, abortSignal)
                        .then((savedLeadToLive: LeadToLive) => {
                            resolve(savedLeadToLive);
                        })
                        .catch((error) => {
                            reject(error.response.data);
                        });
                })
                .catch((error) => {
                    reject(error.response.data);
                });
        });
    }

    public static saveTrialData(dataToSave: LeadToLive, abortSignal: AbortSignal): Promise<LeadToLive> {
        return new Promise<LeadToLive>((resolve, reject) => {
            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.post("/api/system/lead/trial/save", dataToSave, {signal: abortSignal})
                .then((response) => {
                    let nextStep: LeadToLiveStep = this.getCurrentStep(dataToSave);

                    this.getTrialStatus(nextStep, abortSignal)
                        .then((savedLeadToLive: LeadToLive) => {
                            resolve(savedLeadToLive);
                        })
                        .catch((error) => {
                            reject(error.response.data);
                        });
                })
                .catch((error) => {
                    reject(error.response.data);
                });
        });
    }

    public static resetTrialStep(leadToLive: LeadToLive, abortSignal: AbortSignal): Promise<LeadToLive> {
        return new Promise<LeadToLive>((resolve, reject) => {
            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.post<AuthResponse>(`/api/Setup/customer/${leadToLive.customer.id}/trial/reset`).then((response) => {
                RegisterService.changeAuthToken(response.data);
                this.getTrialStatus(leadToLive.currentStep, abortSignal)
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error.message);
                    });
            })
            .catch((error) => {
                reject(error.response.data);
            });
        });
    }

    public static getCountryRegion(): Promise<CountryRegion[]> {
        return new Promise<CountryRegion[]>((resolve, reject) => {
            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.get<CountryRegion[]>("/odata/CountryRegion?$filter=enabled eq true")
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error.response.data);
                });
        });
    }

    public static getPostalCodes(countryCode: string): Promise<PostalCode[]> {
        return new Promise<PostalCode[]>((resolve, reject) => {
            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.get<PostalCode[]>(`/odata/PostalCode?$filter=countryCode eq '${countryCode}'`)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error.response.data);
                });
        });
    }

    public static sendAdminEmail(mail: string, url: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            let message: RegisterGDAPMailMessage = new RegisterGDAPMailMessage();
            message.mailAddress = mail;
            message.url = url;

            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.post("/api/Setup/customer/admin/send/email", message)
                .then((resp) => {
                    resolve(true);
                })
                .catch((err) => {
                    reject(err);
                });
        });

    }

    public static sendCredentialEmail(userId: number): Promise<boolean> {
        return new Promise((resolve, reject) => {

            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.post(`/api/System/lead/trial/sendusercredentialemail/${userId}`)
                .then((resp) => {
                    resolve(true);
                })
                .catch((err) => {
                    reject(err);
                });
        });

    }

    public static validateOtp(otp: string): Promise<boolean> {
        return new Promise((resolve, reject) => {

            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.post(`/api/System/user/otp/validate/${otp}`)
                .then((resp) => {
                    resolve(resp.data);
                })
                .catch((err) => {
                    reject(err.response.data);
                });
        });
    }

    public static generateOtp(): Promise<boolean> {
        return new Promise((resolve, reject) => {

            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.post("/api/System/user/otp/generate")
                .then((resp) => {
                    resolve(resp.data);
                })
                .catch((err) => {
                    reject(err.response.data);
                });
        });
    }

    public static requestHelp(log: ApplicationLogEntry): Promise<boolean> {
        return new Promise((resolve, reject) => {
            console.log(log);
            const instance: AxiosInstance = HttpHelper.buildAxiosInstance();
            instance.post("/odata/application/log/entry", log)
                .then((resp) => {
                    resolve(resp.data);
                })
                .catch((err) => {
                    reject(err.response.data);
                });
        });
    }

    //#endregion API

    //#region Common

    public static handleUserSwitchLogin(code: string, state: string) : Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if(code && state) {
                const sosmartMsLogin: SoSmartMicrosoftLogin = new SoSmartMicrosoftLogin();

                sosmartMsLogin.validateMicrosoftLogin(window.location.origin + window.location.pathname, code, state).then((resp) => {
                    if(this.changeAuthToken(resp))
                        reject("Error: Switch login failed");
                    else
                        resolve();
                })
                .catch((error) => {
                    reject(error);
                });
            }
            else{
                reject("Error: Missing parameters");
            }
        });
    }

    public static changeAuthToken(authResponse: AuthResponse): boolean {
        let tokenClaims: any = jwt(authResponse.value);
        if (tokenClaims.appid == undefined)
            return false;

        let tokenExpiration = new Date(0);
        tokenExpiration.setUTCSeconds(tokenClaims.exp);

        CookieService.removeCookie(HttpConstants.ACCESSTOKEN_COOKIE_NAME);
        CookieService.setCookieValue(HttpConstants.ACCESSTOKEN_COOKIE_NAME, authResponse.value, tokenExpiration, true);

        //Refresh to change the token
        window.location.reload();

        return true;
    }
    
    public static suggestCountry(leadToLive: LeadToLive): string {
        if((leadToLive.lead.country !== "") && (leadToLive.lead.country !== null)){
            return leadToLive.lead.country;
        }

        let hostname: string = window.location.hostname;
        let extension: string | undefined = "";
        if(window.location.hostname.includes('localhost')){
            extension = process.env.REACT_APP_SYSTEM_BACKEND_DEFAULT_HOST_EXTENSION?.substring(1).toUpperCase();
        }
        else{
            extension = hostname.split('.')[2].toUpperCase();
        }

        if(extension !== undefined){
            return extension;
        }

        return "";
    }

    public static getCountryRegionOptions(countryRegions: CountryRegion[]): IComboBoxOption[] {
        let options: IComboBoxOption[] = [];
        countryRegions.forEach((countryRegion: CountryRegion) => {
            options.push({key: countryRegion.countryCode, text: countryRegion.country});
        });

        return options;
    }

    public static getPostalCodeOptions(postalCodes: PostalCode[]): IComboBoxOption[] {
        let options: IComboBoxOption[] = [];
        postalCodes.forEach((postalCodes: PostalCode) => {
            options.push({key: postalCodes.zipCode, text: postalCodes.zipCode + " - " + postalCodes.city});
        });

        return options;
    }
    
    private static generateCompanyAddress(companyData: OpenAPICompanyITAdvancedData): string {
        let address = companyData?.address.registeredOffice;
        if (address?.toponym && address.street && address.streetNumber)
            return address.toponym + ' ' + address.street + ', ' + address.streetNumber;

        return address?.streetName ?? '';
    }

    private static getBalanceSheet(companyData: OpenAPICompanyITAdvancedData): BalanceSheet {
        if (companyData.balanceSheets?.last?.turnover > 0) {
            return companyData.balanceSheets.last;
        }
    
        const balanceSheets: BalanceSheet[] = companyData.balanceSheets?.all || [];
        for (const balanceSheet of balanceSheets) {
            if (balanceSheet.turnover > 0) {
                return balanceSheet;
            }
        }
        
        return balanceSheets[0] || {};
    }

    private static convertStringToDate(dateString: string): Date | null {
        const parsedDate = new Date(dateString);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    //#endregion

    //#region VAT Validation

    public static isValidVAT(vat: string) : boolean {
        // Check if VAT is empty
        if(!vat)
            return false;
        
        // Check 11 digits
        let vatRegex: RegExp = new RegExp("!/^\d{11}$/");
        if (vatRegex.test(vat))
            return false;
        
        let sum: number = 0;
        for (let I = 0; I < 11; I++) {
            let digit: number = parseInt(vat[I]);
            
            // Adds digits in odd and even positions with different logic
            if (I % 2 === 0) {
                sum += digit;
            } 
            else {
                sum += (digit * 2 > 9) ? digit * 2 - 9 : digit * 2;
            }
        }

        // The sum must be divisible by 10
        return sum % 10 === 0;
    }

    public static validateAndGetVATData(leadToLive: LeadToLive) : Promise<LeadToLive> {
        return new Promise<LeadToLive>((resolve, reject) => {
            let currentLeadToLive: LeadToLive = leadToLive;

            if (!this.isValidVAT(currentLeadToLive.lead.vat)) {
                reject(new ErrorResponse(400, this.translate("vat:error")));
                return;
            }
            if (leadToLive.lead.vat === leadToLive.lead.validatedVat){
                resolve(currentLeadToLive);
                return;
            }
            
            const adminService: AdminService = new AdminService();
            adminService.getVatOpenAPIData(currentLeadToLive.lead.vat, currentLeadToLive.lead.id, 0).then((resp)=>{
                if (resp.success) {
                    let companyData: OpenAPICompanyITAdvancedData = resp.data[0];
                    
                    if (companyData?.companyName)
                        currentLeadToLive.lead.companyName = companyData.companyName;
                    if (companyData?.sdiCode)
                        currentLeadToLive.lead.sdi = companyData.sdiCode;
                    if (companyData?.pec)
                        currentLeadToLive.lead.pecEmail = companyData.pec;
                    if (this.generateCompanyAddress(companyData))
                        currentLeadToLive.lead.address = this.generateCompanyAddress(companyData);
                    if (companyData?.address.registeredOffice.town)
                        currentLeadToLive.lead.city = companyData.address.registeredOffice.town;
                    if (companyData?.address.registeredOffice.province)
                        currentLeadToLive.lead.county = companyData.address.registeredOffice.province;
                    if (companyData?.address.registeredOffice.zipCode)
                        currentLeadToLive.lead.zipCode = companyData.address.registeredOffice.zipCode;
                    
                    currentLeadToLive.lead.country = 'IT';

                    const balanceSheet = this.getBalanceSheet(companyData);

                    if (companyData?.atecoClassification?.ateco?.code)
                        currentLeadToLive.lead.atecoCode = companyData.atecoClassification.ateco.code;
                    if (companyData?.atecoClassification?.ateco?.description)
                        currentLeadToLive.lead.atecoDescription = companyData.atecoClassification.ateco.description;

                    if (balanceSheet.turnover)
                        currentLeadToLive.lead.turnover = balanceSheet.turnover;
                    if (balanceSheet.employees)
                        currentLeadToLive.lead.noOfEmployees = balanceSheet.employees;
                    if (balanceSheet.year)
                        currentLeadToLive.lead.balanceYear = balanceSheet.year;
                    if (balanceSheet.netWorth)
                        currentLeadToLive.lead.netWorth = balanceSheet.netWorth;
                    if (balanceSheet.shareCapital)
                        currentLeadToLive.lead.shareCapital = balanceSheet.shareCapital;
                    if (balanceSheet.totalStaffCost)
                        currentLeadToLive.lead.totalStaffCost = balanceSheet.totalStaffCost;
                    if (balanceSheet.totalAssets)
                        currentLeadToLive.lead.totalAssets = balanceSheet.totalAssets;
                    if (balanceSheet.avgGrossSalary)
                        currentLeadToLive.lead.avgGrossSalary = balanceSheet.avgGrossSalary;

                    let companyStartDate = this.convertStringToDate(companyData.startDate);
                    let companyRegistrationDate = this.convertStringToDate(companyData.registrationDate);
                    let companyEndDate = this.convertStringToDate(companyData.endDate);
                    if (companyStartDate)
                        currentLeadToLive.lead.companyStartDate = companyStartDate;
                    if (companyRegistrationDate)
                        currentLeadToLive.lead.companyRegistrationDate = companyRegistrationDate;
                    if (companyEndDate)
                        currentLeadToLive.lead.companyEndDate = companyEndDate;
                    
                    currentLeadToLive.lead.companyDataValidated = false;
                    if(this.checkCompanyData(currentLeadToLive))
                        currentLeadToLive.lead.companyDataValidated = true;

                    currentLeadToLive.lead.validatedVat = currentLeadToLive.lead.vat;
                    resolve(currentLeadToLive);
                }
            })
            .catch((err)=>{
                reject(new ErrorResponse(400, this.translate("vat:error")));
            });
        });
    }

    private static checkCompanyData(leadToLive: LeadToLive): boolean {
        const requiredFields: string[] = ["firstname", "lastname", "companyName", "country", "address", "zipCode", "city", "county"];
        const companyData: Record<string, any> = leadToLive.lead;

        let companyDataValid: boolean = true;
        requiredFields.forEach((field: string, index: number, array: string[]) => {
            if(!companyData[field]){
                companyDataValid = false;
                return;
            }
        });

        return companyDataValid;
    }

    //#endregion VAT Validation


    public static getCurrentStep(leadToLive: LeadToLive) : LeadToLiveStep {
        if(!leadToLive.lead.setupCompanyCompleted) {
            if(leadToLive.legalEntityVatManagement) {
                if((leadToLive.lead.validatedVat !== leadToLive.lead.vat) || (leadToLive.lead.vat === "" || leadToLive.lead.vat === null)){
                    return LeadToLiveStep.VAT;
                }
                else if(!leadToLive.lead.companyDataValidated){
                    return LeadToLiveStep.CompanyData;
                }
                else{
                    return LeadToLiveStep.Welcome;
                }
            }
            else if(!leadToLive.lead.companyDataValidated){
                return LeadToLiveStep.CompanyData;
            }

            switch(leadToLive.currentStep) {
                case LeadToLiveStep.Welcome:
                    return LeadToLiveStep.Goals;
                case LeadToLiveStep.Goals:
                    return LeadToLiveStep.Configuration;
                case LeadToLiveStep.Configuration:
                    return LeadToLiveStep.Migrations;
                case LeadToLiveStep.Migrations:
                    return LeadToLiveStep.Scenario;
                case LeadToLiveStep.Scenario:
                    return LeadToLiveStep.TrialEvaluation;
                default:
                    return LeadToLiveStep.Welcome;
            }
        }
        else {
            if(leadToLive.customer && leadToLive.customer.trialWizardCompleted)
                return LeadToLiveStep.Complete;
            else
                return leadToLive.currentStep;
        }

    }

    public static getStepEndpoint(leadToLive: LeadToLive) : string {
        let params: string = window.location.search + window.location.hash;
        let path: string = "/app/register";
        
        switch(leadToLive.currentStep){
            case LeadToLiveStep.VAT:
                path += "/vat";
                break;
            case LeadToLiveStep.CompanyData:
                path += "/company";
                break;
            case LeadToLiveStep.Welcome:
                path += "/welcome";
                break;
            case LeadToLiveStep.Goals:
                path += "/goals";
                break;
            case LeadToLiveStep.Configuration:
                path += "/configuration";
                break;
            case LeadToLiveStep.Migrations:
                path += "/migrations";
                break;
            case LeadToLiveStep.Scenario:
                path += "/scenario";
                break;
            case LeadToLiveStep.TrialEvaluation:
                path += "/trialEvaluation";
                break;
            case LeadToLiveStep.TenantMode:
                path += "/trial/tenant";
                break;
            case LeadToLiveStep.CspCustomerActive:
                path += "/trial/admin";
                break;
            case LeadToLiveStep.CSPReseller:
                path += "/trial/reseller";
                break;
            case LeadToLiveStep.GDAPAdmin:
                path += "/trial/gdap";
                break;
            case LeadToLiveStep.GDAPDyn365:
                path += "/trial/gdap";
                break;
            case LeadToLiveStep.Complete:
                path = "/app";
                break;
            default:
                path = "/app";
        }

        return path + params;
    }

    public static getErrorMessage(registerErrors: RegisterError, fieldKey?: string): string {
        if(!registerErrors.hasError)
            return ""

        if(fieldKey) {
            let fieldError: RegisterFieldErrors | undefined = registerErrors.fieldErrors.find((fieldError: RegisterFieldErrors) => fieldError.key === fieldKey);
            if(fieldError)
                return fieldError.error;
        }
        else
            return registerErrors.errorMessage;

        return "";
    }

    public static getStepNumber(step: LeadToLiveStep): number {
        const steps = Object.values(LeadToLiveStep).filter(value => typeof value === 'number');
        return steps.indexOf(step);
    }

    public static getTotalStepNumber(): number {
        return Object.values(LeadToLiveStep).filter(value => typeof value === 'number').length - 2;
    }

    public static getProgressPercentage(step: LeadToLiveStep): number {
        const total: number = this.getTotalStepNumber();
        const current: number = this.getStepNumber(step);

        if(total === 0)
            return 0;

        return current / total;
    }

    public static translate(key: string, object?: Object, escape?: boolean): string {
        let basepath: string = "common:register:";
        return t(basepath + key, { ...object, interpolation: { escapeValue: escape } });
    }
}
